import React, { useState, useEffect, useRef, useMemo } from 'react';
import ConfirmationModal from '../../components/modals/confirmationModal';
import UserQueriesTable from '../../components/shared/UserQueriesTable';
import SendMessageModal from '../../components/modals/SendMessageModal';
import { useOnClickOutside } from '../../utils/hooks';
import Senticon from '../../assets/icons/sent-msg.svg'

const styles = {
	mainButton:
		'inline-flex items-center space-x-2 bg-dark-blue max-w-[210px] h-[52px] py-5 px-4 text-base font-semibold leading-100 text-white rounded-md cursor-pointer',
	select:
		'block appearance-none w-full bg-white border-1p border-[#F0F0F0] text-dark-blue py-2 px-4 pr-8 rounded-lg h-[52px]',
	selectSVG: 'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-dark-blue'
};

// --- Filter Dropdown ---
function FilterDropdown({ value, onChange }) {
	const [open, setOpen] = useState(false);
	const panelRef = useRef(null);
	useOnClickOutside(panelRef, () => setOpen(false));

	useEffect(() => {
		const onKey = (e) => e.key === 'Escape' && setOpen(false);
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	const activeCount = (value.status.length || 0) + (value.format.length || 0);

	const toggleToken = (group, token) => {
		const set = new Set(value[group]);
		set.has(token) ? set.delete(token) : set.add(token);
		onChange({ ...value, [group]: Array.from(set) });
	};

	const clearAll = () => onChange({ status: [], format: [] });

	const allStatuses = ['SOLVED', 'PENDING'];

	return (
		<div className="relative" ref={panelRef}>
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				className="inline-flex h-[52px] items-center gap-2 rounded-lg border border-[#F0F0F0] bg-white px-4 text-sm font-medium text-[#180A63]"
				aria-haspopup="menu"
				aria-expanded={open}
			>
				<svg width="20" height="20" viewBox="0 0 24 24" fill="none">
					<path
						d="M0.75 6.125H23.25V8.375H0.75V6.125ZM4.5 11.375H19.5V13.625H4.5V11.375ZM9 16.625H15V18.875H9V16.625Z"
						fill="#180A63"
					/>
				</svg>
				{activeCount > 0 && (
					<span className="ml-1 rounded-full bg-[#180A63] px-2 py-0.5 text-xs font-semibold text-white">
						{activeCount}
					</span>
				)}
			</button>

			{open && (
				<div
					role="menu"
					className="absolute right-0 z-20 mt-2 w-72 rounded-xl border border-gray-200 bg-white p-3 shadow-xl"
				>
					{/* Status */}
					<div className="mb-3">
						<div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
							Status
						</div>
						<div className="flex flex-wrap gap-2">
							{allStatuses.map((s) => (
								<button
									key={s}
									onClick={() => toggleToken('status', s)}
									className={`rounded-full border px-3 py-1 text-xs ${
										value.status.includes(s)
											? 'border-[#180A63] bg-[#180A63] text-white'
											: 'border-gray-300 bg-white text-gray-700'
									}`}
								>
									{s}
								</button>
							))}
						</div>
					</div>

					<div className="my-2 h-px w-full bg-gray-100" />
					<div className="mt-4 flex items-center justify-between">
						<button
							onClick={clearAll}
							className="text-sm font-medium text-gray-500 underline underline-offset-2 hover:text-gray-700"
						>
							Clear all
						</button>
						<button
							onClick={() => setOpen(false)}
							className="rounded-lg bg-[#180A63] px-4 py-2 text-sm font-semibold text-white shadow hover:opacity-95"
						>
							Apply
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export default function UserQueriesPage() {
	const [filters, setFilters] = useState({ status: [], format: [] });

	const [userQueries, setUserQueries] = useState([
		{
			query_id: '#0001',
			email: 'majidali@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'SOLVED',
			solution: 'Resolved earlier.'
		},
		{
			query_id: '#0002',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'PENDING'
		},
		{
			query_id: '#0003',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'PENDING'
		},
		{
			query_id: '#0004',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'SOLVED',
			solution: 'Reinstalled app.'
		},
		{
			query_id: '#0005',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'SOLVED',
			solution: 'Billing updated.'
		},
		{
			query_id: '#0006',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'SOLVED',
			solution: 'Workspace role fixed.'
		},
		{
			query_id: '#0007',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'Need help with my pro accout',
			status: 'PENDING'
		},
		{
			query_id: '#0008',
			email: 'muhammadhaseeb@nesl-it.com',
			message: 'How can I export my document with pro account...',
			status: 'PENDING'
		}
	]);

	// Send flow state
	const [selectedQuery, setSelectedQuery] = useState(null);
	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [draftSolution, setDraftSolution] = useState('');

	const filteredUserQueries = useMemo(() => {
		const base = userQueries.filter((q) =>
			filters.status.length === 0 ? true : filters.status.includes(q.status)
		);
		const seen = new Set();
		return base.filter((q) => {
			const key = `${q.query_id}|${q.email}|${q.message}|${q.status}|${q.solution || ''}`;
			if (seen.has(key)) return false;
			seen.add(key);
			return true;
		});
	}, [userQueries, filters]);

	const handleViewDetails = (query) => {
		setSelectedQuery(query);
		setDraftSolution(query.solution || '');
		setSendModalOpen(true);
	};

	// 1) Close compose modal, 2) open confirmation
	const handleSendMessage = (solutionText) => {
		setDraftSolution(solutionText);
		setSendModalOpen(false); // close first modal
		setConfirmOpen(true); // open "Message sent" modal
	};

	// Confirm -> mark as solved, close confirm; DO NOT open a third modal automatically
	const handleMarkAsSolved = () => {
		if (!selectedQuery) return;
		setUserQueries((prev) =>
			prev.map((q) =>
				q.query_id === selectedQuery.query_id
					? { ...q, status: 'SOLVED', solution: draftSolution }
					: q
			)
		);
		setConfirmOpen(false);
	};

	return (
		<div className="md:py-4 bg-bgColor">
			<div className="flex justify-between flex-col md:flex-row items-center mb-12">
				<div>
					<h1 className="text-dark-blue font-bold text-main leading-100 md:mb-0 mb-5">
						User Queries
					</h1>
				</div>
				<div className="flex space-x-1">
					<div className="relative">
						<select className={styles.select} defaultValue="This month">
							<option>This month</option>
							<option>Last month</option>
							<option>Last 3 months</option>
						</select>
						<div className={styles.selectSVG}>
							<svg
								className="fill-current h-4 w-4"
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 20 20"
							>
								<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
							</svg>
						</div>
					</div>
					{/* Filters dropdown */}
					<FilterDropdown value={filters} onChange={setFilters} />
				</div>
			</div>

			<UserQueriesTable userQueries={filteredUserQueries} onViewDetails={handleViewDetails} />

			{/* Compose / View Solution modal */}
			<SendMessageModal
				isOpen={sendModalOpen}
				onClose={() => setSendModalOpen(false)}
				isImported={false}
				query={selectedQuery}
				onSend={handleSendMessage}
			/>

			{/* Message sent confirmation */}
			<ConfirmationModal
				isOpen={confirmOpen}
				onClose={() => setConfirmOpen(false)}
                icon={<img src={ Senticon} alt="Sent" className="mx-auto" />}
				heading="Message sent"
				description="Your solution message sent to user successfully."
				primaryButtonText="Mark as solved"
				secondaryButtonText="Cancel"
				onPrimaryClick={handleMarkAsSolved}
				onSecondaryClick={() => setConfirmOpen(false)}
			/>
		</div>
	);
}
